import {
	BasicButton,
	Loading,
	SearchInput,
	Skeleton,
	Typography,
} from "@/components";
import { BackAppBar } from "@/components/ui";
import useBackward from "@/hooks/useBackward";
import useContactPeoples from "@/hooks/useContactPeoples";
import useTeamMutation from "@/hooks/useTeamMutation";
import { cn } from "@/libraries/cn";
import { QUERY_KEYS } from "@/libraries/constant";
import { getMembersQueryOptions, getTeamQueryOptions } from "@/queries";
import { AddTeamMemberSearchSchema } from "@/schemas";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { onlineManager, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/teams/$id/edit/members")({
	validateSearch: zodSearchValidator(AddTeamMemberSearchSchema),
	loaderDeps: () => ({ directory: false }),
	loader: ({ context, deps }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureInfiniteQueryData(
				getMembersQueryOptions(deps),
			);
		}
		return context.queryClient.getQueryData([QUERY_KEYS.GET_MEMBERS, deps]);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation("dashboard");
	const navigate = Route.useNavigate();
	const loaderDeps = Route.useLoaderDeps();
	const searchQ = Route.useSearch({
		select: (state) => state.q ?? "",
	});
	const teamId = Route.useParams({
		select: (state) => state.id,
	});
	const onBack = useBackward();
	const [loadMoreRef, isLoadMore] = useInView();
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const {
		data: { team },
	} = useSuspenseQuery(getTeamQueryOptions(teamId));
	const contacts = useContactPeoples({ ...loaderDeps, excludeProfile: true });

	const { update } = useTeamMutation();

	const peoples = useMemo(() => {
		if (!contacts.peoples) return [];
		const teamUserIds = team.users.map((user) => user.id);
		const teamPendingUserIds = team.pending_invitations.map((p) => p.id);
		const teamExisitingUserIds = [...teamUserIds, ...teamPendingUserIds];
		const uninvitedPeoples = contacts.peoples.filter(
			(p) => !teamExisitingUserIds.includes(p.id),
		);
		if (!searchQ) return uninvitedPeoples;
		return uninvitedPeoples.filter((p) =>
			p.fullname.toLowerCase().includes(searchQ.toLowerCase()),
		);
	}, [contacts.peoples, team.users, team.pending_invitations, searchQ]);

	const onClickNext = useCallback(
		async (e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			try {
				await update.mutateAsync({
					id: teamId,
					name: team.name,
					directory: team.directory,
					user_ids: [...team.users.map((u) => u.id), ...selectedIds],
				});
				return onBack();
			} catch (error) {
				return toast.error(t("dashboard:teams.addMemberFailed"));
			}
		},
		[update, selectedIds, teamId, team, onBack, t],
	);

	const onClickSelectAll = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			const isSelectedAll = peoples.every((people) =>
				selectedIds.includes(people.id),
			);
			if (isSelectedAll) return setSelectedIds([]);
			const peopleIds = peoples.map((people) => people.id);
			return setSelectedIds(Array.from(new Set([...peopleIds])));
		},
		[peoples, selectedIds],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (isLoadMore) contacts.fetchNextPage();
	}, [isLoadMore]);

	return (
		<div className="h-full flex flex-col overflow-hidden">
			<BackAppBar
				title={t("dashboard:teams.addTeam")}
				subTitle={t("dashboard:teams.addMembers")}
			>
				<div className="container pb-2 flex flex-col gap-2">
					<div className="flex flex-row justify-between items-center">
						<Typography fontWeight={700}>
							{t("dashboard:teams.contacts")}
						</Typography>
						{peoples.length ? (
							<Button onClick={onClickSelectAll}>
								{t("dashboard:teams.selectAll")}
							</Button>
						) : null}
					</div>
					{peoples.length ? (
						<SearchInput
							name="search"
							label={t("dashboard:teams.search")}
							placeholder={t("dashboard:teams.search")}
							value={searchQ}
							onChange={(e) =>
								navigate({
									replace: true,
									search: {
										q: e.target.value || undefined,
									},
								})
							}
						/>
					) : null}
				</div>
			</BackAppBar>
			<div className="relative flex-1 flex flex-col overflow-hidden">
				<div className="relative container flex-1 flex flex-col overflow-y-auto">
					<List disablePadding className="divide-y divide-gray-light">
						{peoples.length ? (
							<>
								{peoples.map((people) => (
									<ListItem
										key={people.id}
										disableGutters
										className={cn({
											"bg-primary-light": selectedIds.includes(people.id),
										})}
									>
										<ListItemButton dense>
											<ListItemIcon>
												<Checkbox
													value={people.id}
													checked={selectedIds.includes(people.id)}
													edge="start"
													tabIndex={-1}
													disableRipple
													className="peer:"
													onChange={(e) =>
														setSelectedIds(
															e.target.checked
																? [...selectedIds, people.id]
																: selectedIds.filter((id) => id !== people.id),
														)
													}
												/>
											</ListItemIcon>
											<ListItemAvatar>
												<Avatar
													alt={people.first_name || "L"}
													className="!bg-gray-light !text-dark"
													src={people.avatar ?? "/broken-image.jpg"}
												/>
											</ListItemAvatar>
											<ListItemText primary={people.fullname} />
										</ListItemButton>
									</ListItem>
								))}
							</>
						) : (
							<ListItem disableGutters>
								<ListItemText
									primary={
										<Typography
											fontWeight={700}
											align="center"
											className="!text-gray-200"
										>
											{searchQ
												? t("dashboard:teams.noResultsFor", { query: searchQ })
												: t("dashboard:teams.noContacts")}
										</Typography>
									}
								/>
							</ListItem>
						)}
						{contacts.hasNextPage && !contacts.isFetchingNextPage ? (
							<ListItem ref={loadMoreRef} disableGutters>
								<Skeleton className="w-full h-12" />
							</ListItem>
						) : null}
					</List>
				</div>
				{selectedIds.length ? (
					<div className="container py-4 shadow grid grid-cols-2 justify-between items-center">
						<Typography fontWeight={500}>
							{t("dashboard:teams.contactsSelected", {
								count: selectedIds.length,
							})}
						</Typography>
						<BasicButton
							variant="contained"
							color="secondary"
							className="w-full !h-12 text-sm font-semibold !shadow-none"
							disable={update.isPending}
							onClick={onClickNext}
						>
							{t("dashboard:teams.next")}
						</BasicButton>
					</div>
				) : null}
			</div>
			<Loading status={update.isPending} />
		</div>
	);
}
